import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { AlertTriangle, Plus, Loader2 } from "lucide-react";

const CATEGORIES = ["Operational", "Security", "Compliance", "Financial", "Reputational", "Other"];

const STATUS_STYLES = {
  pending: "text-amber-600 border-amber-500/40 bg-amber-500/10",
  triaged: "text-sky-600 border-sky-500/40 bg-sky-500/10",
  dismissed: "text-muted-foreground border-border bg-muted/30",
};

export default function PortalRisks() {
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Operational" });

  const load = () => api.get("/employee-portal/risk-submissions").then((r) => setItems(r.data.submissions));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.description) return;
    setSaving(true);
    try {
      await api.post("/employee-portal/risk-submissions", form);
      toast({ title: "Risk submitted" });
      setOpen(false);
      setForm({ title: "", description: "", category: "Operational" });
      load();
    } finally { setSaving(false); }
  };

  if (!items) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portal-risks">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Flag a Risk</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" data-testid="portal-new-risk-btn"><Plus className="w-3.5 h-3.5" /> New risk</Button>
          </DialogTrigger>
          <DialogContent data-testid="portal-risk-dialog">
            <DialogHeader><DialogTitle>Flag a risk</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="portal-risk-title-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="portal-risk-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="portal-risk-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} data-testid="portal-risk-submit-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {items.map((v) => (
          <Card key={v.id} data-testid={`portal-risk-row-${v.id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <AlertTriangle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">{v.title}</div>
                  <div className="text-[12px] text-muted-foreground">{v.description}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{v.category}</div>
                </div>
              </div>
              <Badge variant="outline" className={`flex-shrink-0 capitalize ${STATUS_STYLES[v.status] || ""}`}>{v.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-[13px] text-muted-foreground">No risks submitted yet.</p>}
      </div>
    </div>
  );
}
